import type { HookReturnValue, BoundActions, Action } from 'react-sweet-state';

import type { OriginQuery, AllTeamsQuery } from '@atlaskit/teams-client';
import type {
	TeamMembership,
	TeamWithImageUrls,
	User,
	TeamMembershipSettings,
	TeamAgentAssociation,
} from '@atlaskit/teams-client/types';

import { useTeamProfileDataState } from '../../main';
import type { TeamProfileState, TeamAppState, StoreApi } from '../../types';
import { useOrCollectData } from '../use-or-collect-data';
import type { TeamsPermissions } from '../use-team-visibility';

export const useTeamProfileData = (): HookReturnValue<
	TeamProfileState,
	BoundActions<
		TeamAppState,
		{
			setTeamClientRootUrl: (rootUrl: string) => Action<TeamAppState>;
			collectTeamData: (
				teamId: string,
				forceRefresh?: boolean,
			) => ({ dispatch, getState }: StoreApi) => Promise<void>;
			restoreTeam: (
				teamId: string,
				options?: {
					fetchTeamData?: boolean;
				},
			) => ({ dispatch }: StoreApi) => Promise<void>;
			fetchTeamMembersNextPage: (
				teamId: string,
				count?: number,
			) => ({ dispatch, getState }: StoreApi) => Promise<void>;
			refreshRequestingToJoinUsers: (
				teamId: string,
			) => ({ dispatch, getState }: StoreApi) => Promise<TeamMembership[]>;
			prefillTeamData: (
				teamId: string,
				teamData: TeamWithImageUrls,
				members: User[],
				currentUser: User,
			) => ({ getState, setState, dispatch }: StoreApi) => void;
			updateTeamProfile: (
				teamId: string,
				data: {
					displayName?: string;
					description?: string;
					headerImageId?: string;
					membershipSettings?: TeamMembershipSettings;
				},
			) => ({ dispatch, getState, setState }: StoreApi) => Promise<TeamWithImageUrls>;
			acceptAllJoinRequests: (
				teamId: string,
			) => ({ dispatch, getState }: StoreApi) => Promise<void>;
			declineAllJoinRequests: (
				teamId: string,
			) => ({ dispatch, getState }: StoreApi) => Promise<void>;
			removeTeamMember: (
				teamId: string,
				memberId: string,
				permissions?: Partial<TeamsPermissions>,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
			removeTeamAgent: (
				teamId: string,
				agentAtlassianAccountId: string,
				permissions?: Partial<TeamsPermissions>,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
			removeTeamAgentAssociation: (
				teamId: string,
				agentId: string,
				onRemoveAgentSuccess?: (agentId: string, teamId: string) => void,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
			revokeTeamInvite: (
				teamId: string,
				memberId: string,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
			joinTeam: (
				teamId: string,
				originQuery: OriginQuery,
				user: User,
			) => ({ dispatch }: StoreApi) => Promise<TeamMembership>;
			cancelJoinRequest: (teamId: string) => ({ getState, setState }: StoreApi) => Promise<void>;
			acceptTeamInvitation: (
				teamId: string,
				currentUserId: string,
			) => ({ dispatch, getState }: StoreApi) => Promise<void>;
			declineTeamInvitation: (teamId: string) => ({ getState }: StoreApi) => Promise<void>;
			inviteUsersToTeam: (
				teamId: string,
				members: {
					id: string;
					email?: string;
					avatarUrl?: string;
					name: string;
					title?: string;
				}[],
				originQuery: OriginQuery,
			) => ({
				dispatch,
				getState,
				setState,
			}: StoreApi) => Promise<Required<TeamMembership>[] | undefined>;
			inviteAgentsToTeam: (
				teamId: string,
				members: {
					id: string;
					email?: string;
					avatarUrl?: string;
					name: string;
				}[],
			) => ({ getState, setState }: StoreApi) => Promise<Required<TeamMembership>[] | undefined>;
			addAgentsToTeam: (
				teamId: string,
				agents: {
					id: string;
					avatarUrl?: string;
					name: string;
				}[],
			) => ({ getState, setState }: StoreApi) => Promise<Required<TeamAgentAssociation>[]>;
			approveJoinRequest: (
				teamId: string,
				memberId: string,
				user: User,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
			declineJoinRequest: (
				teamId: string,
				memberId: string,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
			deleteTeam: (teamId: string) => ({ getState, setState }: StoreApi) => Promise<void>;
			archiveTeam: (
				teamId: string,
			) => ({ getState, setState }: StoreApi) => Promise<TeamWithImageUrls | undefined>;
			unarchiveTeam: (
				teamId: string,
			) => ({ getState, setState }: StoreApi) => Promise<TeamWithImageUrls | undefined>;
			unlinkExternalTeam: (teamId: string) => ({ getState, setState }: StoreApi) => Promise<void>;
			getAllTeams: (
				allTeamsQuery: AllTeamsQuery,
				keepExistingData?: boolean,
			) => ({ dispatch }: StoreApi) => Promise<void>;
			updateTeamMembershipSettings: (
				teamId: string,
				membershipSettings: TeamMembershipSettings,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
		}
	>
> => {
	const teamProfileDataStateSlice = useTeamProfileDataState();
	return useOrCollectData(teamProfileDataStateSlice);
};
